import {useState, useCallback} from 'react'
import {plusIconBold, cancelIcon} from '@img/index.js'
import {formatPrice} from '@/utils/functions'
import CardRow from './CardRow.jsx'
import AddCardPopup from './PageModals/ComponentPopups/PaymentMethodsPopup.jsx'
import RetryHoldButtons from '@/customComponents/RideView/RetryHoldButtons.jsx'

export default function PaymentMethodsCard({savedCards, selectedCardId, setSelectedCardId, onCardAdded, policy = {canEdit: true, blockedReason: null}, walletAmountCents = 0, readOnly = false, hideAddCard = false, selectedOnly = false, trip = null}) {
    const allCards = savedCards?.length ? savedCards : (trip?.passenger?.payment_methods || [])
    // On the edit/confirm page we surface only the selected card (no other selectables, no add-card).
    // Falls back to the full list when there's no selected card yet, so the user can still pick/add one.
    const selectedCard = allCards.find(c => String(c.id) === String(selectedCardId))
    const showSelectedOnly = selectedOnly && !!selectedCard
    /*
     * Read-only, showing only the selected card, and the trip names none: say that,
     * rather than listing every card the passenger has saved with nothing ticked. That
     * list answers a question nobody asked and reads as "one of these will be charged".
     */
    const nothingChosen = readOnly && selectedOnly && !selectedCard
    const cards = showSelectedOnly ? [selectedCard] : allCards
    const paymentHoldFailed = trip?.payment_hold?.is_authorized === false || trip?.payment_hold?.is_captured === false
    const hasWallet = (walletAmountCents ?? 0) > 0
    const selectDisabled = readOnly || !policy.canEdit
    const showAddCardButton = !hideAddCard && !readOnly && !showSelectedOnly
    const [showAddCard, setShowAddCard] = useState(false)

    const handleCardAdded = useCallback((newCard) => {
        onCardAdded?.(newCard)
        setSelectedCardId(newCard.id)
        setShowAddCard(false)
    }, [onCardAdded, setSelectedCardId])

    return (
        <div className="bg-white p-5 rounded-(--radius-2xl)">
            <h6 className="text-lg font-semibold mb-4!">Payment Methods</h6>

            <div className="flex flex-col gap-3">
                {nothingChosen ? (
                    <p className="text-(--text-secondary) text-sm mb-0!">
                        No payment method on this booking yet.
                    </p>
                ) : cards.length > 0 ? cards.map(card => (
                    <CardRow key={card.id} card={card}
                             isSelected={String(selectedCardId) === String(card.id)}
                             onSelect={setSelectedCardId} disabled={selectDisabled}
                             dimmed={!readOnly && !policy.canEdit}/>
                )) : (
                    <p className="text-(--text-secondary) text-sm mb-0!">No saved cards</p>
                )}

                {showAddCardButton && (
                    <button type="button" disabled={!policy.canEdit} onClick={() => setShowAddCard(true)}
                            className="w-full flex items-center gap-3 p-3.5 rounded-[16px] border-0 bg-(--bg-grey) text-left cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                        <span className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0">
                            <img src={plusIconBold} alt="" className="w-5"/>
                        </span>
                        <span className="font-semibold text-sm">Add new card</span>
                    </button>
                )}
            </div>

            {hasWallet && (
                <div className="mt-3">
                    <span className="bg-(--accent-green-light) text-(--text-primary) rounded-full px-6 py-2.5 inline-flex items-center gap-2 text-sm">
                        Credit: {formatPrice(walletAmountCents, {decimals: 2})}
                    </span>
                </div>
            )}

            {!policy.canEdit && policy.blockedReason && (
                <p className="text-(--text-secondary) text-xs mt-2 mb-0!">{policy.blockedReason}</p>
            )}

            {paymentHoldFailed && (
                <>
                    <div className="flex items-center gap-2 text-lg mb-0 mt-3">
                        <img alt="failed" src={cancelIcon} className="mr-2"/>
                        <span>Credit card payment failed, retry or add new card</span>
                    </div>
                    <RetryHoldButtons trip={trip}/>
                </>
            )}

            {showAddCardButton && (
                <AddCardPopup
                    isOpen={showAddCard}
                    onClose={() => setShowAddCard(false)}
                    onCardAdded={handleCardAdded}
                />
            )}
        </div>
    )
}
